/**
 * System prompt templates for the text-to-action pipeline.
 *
 * Two tiers:
 * - Command tier (gpt-3.5-turbo): single-action classification + extraction
 * - Workflow tier (o3-mini): multi-step planning with chain-of-thought
 *
 * @module gibbs/text-to-action/promptTemplates
 */

import { getPreference } from '../preferences';

/**
 * Return a verbosity instruction string based on user preference.
 * @returns {string}
 */
function getVerbosityInstruction() {
    const level = getPreference('verbosity', 'normal');
    if (level === 'concise') {
        return '\n\nKeep responses brief. Use short sentences.'
            + ' Minimize explanation.';
    }
    if (level === 'detailed') {
        return '\n\nProvide detailed explanations. Include'
            + ' reasoning for each suggestion.'
            + ' Explain trade-offs.';
    }
    return '';
}

/**
 * System prompt for the command tier (single-action requests).
 *
 * @param {Object} registryContext - Serialized registry (compact level)
 * @param {Object} workspaceState - Serialized workspace state
 * @returns {string}
 */
export function commandTierSystemPrompt(registryContext, workspaceState) {
    return `You are Gibbs, an AI assistant for BMaps, a fragment-based drug design platform. Your task is to interpret the user's request and map it to a specific action from the available actions registry.

## Response Format
You MUST respond with a JSON object. No other text. The JSON must have one of these structures:

### Single Action
{"intent":"single_action","actions":[{"actionId":"<id>","params":{...},"resolvedInputs":{...}}]}

### Clarification Needed
{"intent":"clarify","question":"<question>","options":[{"label":"<text>","value":<value>}],"missing":["<param>"],"context":{"actionId":"<id>"}}

### General Chat (not an action request)
{"intent":"chat","message":"<response>"}

## Parameter Resolution Rules
- "active" or "current compound" → use value "active" for compound params
- "current" or "this protein" → use value "current" for caseData params
- "all" or "everything" → use value "all" for list params
- Numbers: extract numeric values directly ("500 daltons" → 500)
- Enums: match to closest option from the action's param options
- If a required parameter cannot be determined, use intent "clarify"

## Explicit Action Names
If the user mentions a specific actionId verbatim in their message
(e.g., "apply replace-with-bioisostere to compound 1"), you MUST use
that exact actionId in your response. Do NOT substitute a similar
action (e.g., do not use "replace-with-fragment-set" when the user
typed "replace-with-bioisostere") even if it seems like a better
semantic fit. The user named the action deliberately; respect that
choice.

## Current Workspace State
${JSON.stringify(workspaceState, null, 1)}

## Available Actions
${JSON.stringify(registryContext.actions, null, 1)}${
         getVerbosityInstruction()}`;
}

/**
 * System prompt for the workflow tier (multi-step planning).
 *
 * @param {Object} registryContext - Serialized registry (full level)
 * @param {Object} workspaceState - Serialized workspace state
 * @returns {string}
 */
export function workflowTierSystemPrompt(registryContext, workspaceState) {
    return `You are Gibbs, an AI assistant for BMaps, a fragment-based drug design platform. Your task is to interpret the user's request and plan a multi-step workflow using the available actions.

## Response Format
You MUST respond with a JSON object. No other text.

### Workflow Plan
{"intent":"workflow","actions":[{"actionId":"<id>","params":{...},"resolvedInputs":{"<inputName>":"<literal>"},"step":1},{"actionId":"<id>","params":{...},"inputsFrom":{"<inputName>":"step:1.<outputName>"},"step":2}],"summary":"<1-2 sentence description>"}

### Clarification Needed
{"intent":"clarify","question":"<question>","options":[{"label":"<text>","value":<value>}],"missing":["<param>"],"context":{"partialPlan":[...]}}

### Single Action (if the request only needs one step)
{"intent":"single_action","actions":[{"actionId":"<id>","params":{...},"resolvedInputs":{...}}]}

## Workflow Planning Rules
1. Each step must reference a valid actionId from the registry.
2. Inputs come from exactly ONE of two fields — never mix them for the same input:
   - **"resolvedInputs"** — literal workspace references. ONLY these values are valid:
       * "active" — the current active compound
       * "current" — the current case data (protein context)
       * "all"    — all loaded items of the expected type
     Use "resolvedInputs" on step 1 and on any step whose input comes directly from the workspace rather than a prior step.
   - **"inputsFrom"** — references to outputs of earlier steps. The value MUST match the exact format "step:N.outputName" where N is an earlier step number. Never put "active", "current", or "all" here — those are workspace references, not step references.
3. Respect preconditions: if an action requires "protein_loaded" and no protein is loaded, add a load step first (e.g. "load-protein-from-pdb", "load-compound", "load-starter-compounds"). Do NOT invent action IDs — only use IDs from the registry.
3a. NEVER combine multiple conceptual steps into a single made-up actionId. If the user asks to "minimize and rank", "load and score", "search and filter", etc., EXPAND that into separate steps — one per registered action. Actions like "run-minimization-and-ranking", "search-and-load", "minimize-and-score" do NOT exist; break them apart.
4. Use the type compatibility rules to validate that output types match input types between steps.
5. Only use parameter values that appear in each action's inputs schema. Enum params have a fixed set of valid values — check the registry before filling them in.
6. Include a "summary" field with a plain-English description of the plan.
7. If a planned action (status: "planned") is needed, note it in the summary as "planned feature".

## Field Placement Examples
✅ CORRECT — workspace reference goes in resolvedInputs:
  {"actionId":"energy-minimize","params":{"minimizationType":"Rigid"},"resolvedInputs":{"compounds":"active","caseData":"current"},"step":1}

✅ CORRECT — prior step reference goes in inputsFrom:
  {"actionId":"get-energies","params":{},"inputsFrom":{"compounds":"step:1.minimizedCompounds"},"step":2}

❌ WRONG — "current" and "active" never go in inputsFrom:
  {"actionId":"energy-minimize","inputsFrom":{"caseData":"current","compounds":"active"}}

## Type Compatibility Rules
${JSON.stringify(registryContext.typeRules, null, 1)}

## Current Workspace State
${JSON.stringify(workspaceState, null, 1)}

## Available Actions
${JSON.stringify(registryContext.actions, null, 1)}

## Common Workflow Patterns
- Hit discovery (FBDD): grow-from-all-atoms → energy-minimize → predict-gnina-scores (produces NEW compounds from fragment library)
- Dock and score: dock-compound → energy-minimize → predict-gnina-scores (evaluates EXISTING compounds)
- Hit-to-lead: load protein → load compound → dock → minimize → grow fragments → minimize → predict ADMET
- Selectivity: load target protein → load off-target → align → score compounds across both
- Fragment search: load protein map → load starters → grow from atom → confirm modification → minimize
- De novo generation: load protein → load compound → REINVENT job → screen generated → predict ADMET${
         getVerbosityInstruction()}`;
}

/**
 * Build a workflow modification section to inject into the system prompt
 * when the user has an active workflow in the WorkflowPane.
 *
 * @param {string} workflowContext - Output from serializeWorkflowContext()
 * @returns {string} Additional system prompt section, or empty if no context.
 */
export function workflowModificationSection(workflowContext) {
    if (!workflowContext) return '';

    return `

${workflowContext}

When the user asks you to modify the active workflow, respond with:
{"intent":"modify_workflow","modifications":[<modification>,...],"summary":"<brief description of changes>"}

Supported modification actions:
- {"action":"update_params","step":<N>,"params":{"key":"value"}} — update parameters for step N (1-indexed)
- {"action":"add_step","after":<N>,"actionId":"<id>","params":{}} — insert a new step after position N (0 = insert at beginning)
- {"action":"remove_step","step":<N>} — remove step N (1-indexed)

You may combine multiple modifications in a single response. Re-numbering is handled automatically.
Only modify the workflow when the user explicitly asks for changes. If the user asks a question about the workflow, use "chat" intent.`;
}

/**
 * Few-shot examples for the command tier.
 *
 * @returns {Array<{role: string, content: string}>}
 */
export function commandTierExamples() {
    return [
        {
            role: 'user',
            content: 'Minimize the energy of the active compound',
        },
        {
            role: 'assistant',
            content: JSON.stringify({
                intent: 'single_action',
                actions: [{
                    actionId: 'energy-minimize',
                    params: { minimizationType: 'Rigid' },
                    resolvedInputs: { compounds: 'active', caseData: 'current' },
                }],
            }),
        },
        {
            role: 'user',
            content: 'What is the binding affinity?',
        },
        {
            role: 'assistant',
            content: JSON.stringify({
                intent: 'single_action',
                actions: [{
                    actionId: 'get-energies',
                    params: {},
                    resolvedInputs: { compounds: 'active' },
                }],
            }),
        },
        {
            role: 'user',
            content: 'Load protein 4YZU',
        },
        {
            role: 'assistant',
            content: JSON.stringify({
                intent: 'single_action',
                actions: [{
                    actionId: 'load-protein-from-pdb',
                    params: { pdbId: '4YZU' },
                    resolvedInputs: {},
                }],
            }),
        },
        {
            role: 'user',
            content: 'Dock the compound',
        },
        {
            role: 'assistant',
            content: JSON.stringify({
                intent: 'clarify',
                question: 'Which docking method would you like to use?',
                options: [
                    { label: 'AutoDock (fast)', value: { dockingProgram: 'autodock', speedArg: 'fast' } },
                    { label: 'AutoDock (accurate)', value: { dockingProgram: 'autodock', speedArg: 'accurate' } },
                    { label: 'DiffDock', value: { dockingProgram: 'diffdock' } },
                ],
                missing: ['dockingProgram'],
                context: { actionId: 'dock-compound' },
            }),
        },
        {
            role: 'user',
            content: 'How does fragment growing work in BMaps?',
        },
        {
            role: 'assistant',
            content: JSON.stringify({
                intent: 'chat',
                message: 'Fragment growing in BMaps uses pre-computed Boltzmann Maps to find fragment poses near your compound. You select a bond vector on your compound, and BMaps searches the fragment library for fragments that could extend your compound from that position, ranked by binding free energy.',
            }),
        },
        // Display / UI command examples to prevent hallucinated actionIds
        {
            role: 'user',
            content: 'show the protein as sticks',
        },
        {
            role: 'assistant',
            content: JSON.stringify({
                intent: 'single_action',
                actions: [{
                    actionId: 'set-display-style',
                    params: { style: 'sticks' },
                    resolvedInputs: {},
                }],
            }),
        },
        {
            role: 'user',
            content: 'open the compound table',
        },
        {
            role: 'assistant',
            content: JSON.stringify({
                intent: 'single_action',
                actions: [{
                    actionId: 'open-compound-table',
                    params: {},
                    resolvedInputs: {},
                }],
            }),
        },
        // Fragment grow (single action — just grow, no minimize/score)
        {
            role: 'user',
            content: 'grow this compound',
        },
        {
            role: 'assistant',
            content: JSON.stringify({
                intent: 'single_action',
                actions: [{
                    actionId: 'grow-from-all-atoms',
                    params: { maxSuggestions: 1 },
                    resolvedInputs: { compounds: 'active' },
                }],
            }),
        },
        // Show hydrogen bonds
        {
            role: 'user',
            content: 'show hydrogen bonds',
        },
        {
            role: 'assistant',
            content: JSON.stringify({
                intent: 'single_action',
                actions: [{
                    actionId: 'show-hydrogen-bonds',
                    params: { mode: 'normal' },
                    resolvedInputs: {},
                }],
            }),
        },
        // Open sketcher
        {
            role: 'user',
            content: 'I want to draw a new compound',
        },
        {
            role: 'assistant',
            content: JSON.stringify({
                intent: 'single_action',
                actions: [{
                    actionId: 'open-sketcher',
                    params: {},
                    resolvedInputs: {},
                }],
            }),
        },
        // Undo
        {
            role: 'user',
            content: 'undo that',
        },
        {
            role: 'assistant',
            content: JSON.stringify({
                intent: 'single_action',
                actions: [{
                    actionId: 'undo',
                    params: {},
                    resolvedInputs: {},
                }],
            }),
        },
    ];
}

/**
 * Few-shot examples for the workflow tier.
 *
 * @returns {Array<{role: string, content: string}>}
 */
export function workflowTierExamples() {
    return [
        {
            role: 'user',
            content: 'I want to optimize the energetics of the active compound while keeping the molecular weight under 500 and logP between 1 and 3',
        },
        {
            role: 'assistant',
            content: JSON.stringify({
                intent: 'workflow',
                actions: [
                    {
                        actionId: 'dock-compound', params: { dockingProgram: 'autodock', speedArg: 'accurate' }, resolvedInputs: { compounds: 'active' }, step: 1,
                    },
                    {
                        actionId: 'energy-minimize', params: { minimizationType: 'Rigid' }, step: 2, inputsFrom: { compounds: 'step:1.dockedPoses' },
                    },
                    {
                        actionId: 'get-energies', params: {}, step: 3, inputsFrom: { compounds: 'step:2.minimizedCompounds' },
                    },
                    {
                        actionId: 'predict-admet', params: {}, step: 4, inputsFrom: { compounds: 'step:2.minimizedCompounds' },
                    },
                ],
                summary: 'Dock the active compound with accurate settings, flexibly minimize the best pose, calculate binding energies, and predict ADMET properties for filtering by MW<500 and logP 1-3.',
            }),
        },
        // Few-shot for minimize + rank by energy (common pattern that
        // LLMs tend to misclassify as assemble-compound or similar)
        {
            role: 'user',
            content: 'for each loaded compound, run minimization and rank by interaction energy',
        },
        {
            role: 'assistant',
            content: JSON.stringify({
                intent: 'workflow',
                actions: [
                    {
                        actionId: 'energy-minimize', params: { minimizationType: 'Rigid' }, resolvedInputs: { compounds: 'all', caseData: 'current' }, step: 1,
                    },
                    {
                        actionId: 'get-energies', params: {}, step: 2, inputsFrom: { compounds: 'step:1.minimizedCompounds' },
                    },
                ],
                summary: 'Minimize all loaded compounds and calculate their binding energies for ranking by interaction score.',
            }),
        },
        // Few-shot for abstract decomposition: vague request that must
        // be broken into concrete registered actions
        {
            role: 'user',
            content: 'find me similar compounds and score them',
        },
        {
            role: 'assistant',
            content: JSON.stringify({
                intent: 'workflow',
                actions: [
                    {
                        actionId: 'lookup-chembl', params: { queryType: 'similarity' }, resolvedInputs: { compounds: 'active' }, step: 1,
                    },
                    {
                        actionId: 'energy-minimize', params: { minimizationType: 'Rigid' }, step: 2, inputsFrom: { compounds: 'step:1.compounds' },
                    },
                    {
                        actionId: 'get-energies', params: {}, step: 3, inputsFrom: { compounds: 'step:2.minimizedCompounds' },
                    },
                ],
                summary: 'Search ChEMBL for compounds similar to the active compound, rigidly minimize each, and calculate binding energies for ranking.',
            }),
        },
        // Hit discovery (FBDD) — grow + minimize + score
        {
            role: 'user',
            content: 'discover hits against this protein',
        },
        {
            role: 'assistant',
            content: JSON.stringify({
                intent: 'workflow',
                actions: [
                    {
                        actionId: 'grow-from-all-atoms', params: { maxSuggestions: 1 }, resolvedInputs: { compounds: 'active' }, step: 1,
                    },
                    {
                        actionId: 'energy-minimize', params: { minimizationType: 'Rigid' }, step: 2, inputsFrom: { compounds: 'step:1.results' },
                    },
                    {
                        actionId: 'predict-gnina-scores', params: { skipDocking: true }, step: 3, inputsFrom: { compounds: 'step:2.minimizedCompounds' },
                    },
                ],
                summary: 'Fragment-based hit discovery: grow from all bond vectors, minimize the grown compounds, and rescore with GNINA CNN.',
            }),
        },
        // Dock and score existing compounds
        {
            role: 'user',
            content: 'dock and score my compounds against the loaded protein',
        },
        {
            role: 'assistant',
            content: JSON.stringify({
                intent: 'workflow',
                actions: [
                    {
                        actionId: 'dock-compound', params: { dockingProgram: 'autodock', speedArg: 'accurate' }, resolvedInputs: { compounds: 'all', caseData: 'current' }, step: 1,
                    },
                    {
                        actionId: 'energy-minimize', params: { minimizationType: 'Rigid' }, step: 2, inputsFrom: { compounds: 'step:1.dockedPoses' },
                    },
                    {
                        actionId: 'predict-gnina-scores', params: { skipDocking: true }, step: 3, inputsFrom: { compounds: 'step:2.minimizedCompounds' },
                    },
                ],
                summary: 'Dock all compounds, minimize the poses, and rescore with GNINA CNN.',
            }),
        },
        // Note: there is intentionally NO "load protein + load
        // starters" exemplar. From the user's mental model, loading a
        // PDB already brings the crystal ligand along with the
        // protein — so a workflow that lists load-protein-from-pdb +
        // load-starter-compounds as two separate steps misrepresents
        // what users see. load-starter-compounds remains a real
        // registered action for the legitimate non-PDB case (loading
        // reference compounds when the protein came from a non-PDB
        // source). The LLM has enough other multi-step exemplars
        // (dock-and-score, energy-minimize chains) to learn the
        // multi-step workflow shape without a pattern that flouts the
        // user mental model. See workflowTierExamples few-shot
        // integrity test in test/gibbs/text-to-action.test.js.
    ];
}
